import { logger } from '@fe/core';
import {
    DrawingObjectsService,
    FilterGroupsService,
    MonitorSettingsService,
    NotesService,
    SettingsService,
    ViewsService
} from '../../../services/axios/axios-api-v2-generated';
import { ThresholdsCustomEventEntry } from '../../../types/thresholds';
import { type RootState, startSyncLoop, stopSyncLoop } from '../slices';
import { initDrawingObjects, updateDrawingObjects } from '../slices/drawing-objects-slice';
import { processFilterGroupUpdates, setFilterGroupsFromList } from '../slices/filter-groups-slice';
import { setCustomEvents, setFirstSyncCompleted, setMapUrlReplacement, setMonitorSettings, setSlideshow } from '../slices/global-slice';
import { setNotesSummary } from '../slices/notes-slice';
import { initializeSettings } from '../slices/settings-slice';
import { processViewUpdates, setViews } from '../slices/views-slice';
import { initializeWorkspaceFromSettings } from '../slices/workspace-slice';
import { AppDispatch } from '../store';
import { startAppListening } from './listener-middleware';

const UPDATE_PERIOD_SECONDS = 30;

export function startSyncListen() {
    startAppListening({
        actionCreator: startSyncLoop,
        effect: async (_, api) => {
            api.cancelActiveListeners();
            const stopPromise = api.condition(stopSyncLoop.match);
            stopPromise.then(api.cancel);

            let firstSync = true;
            const states = new Array(syncTasks.length).fill(undefined);
            try {
                while (true) {
                    for (let i = 0; i < syncTasks.length; i++) {
                        api.throwIfCancelled();
                        const item = syncTasks[i];
                        try {
                            // TODO: change to Promise.all for speed
                            states[i] = await item.run({
                                dispatch: api.dispatch,
                                getState: api.getState,
                                firstSync: firstSync,
                                state: states[i]
                            });
                        } catch (err) {
                            logger.error("Error in sync task '%s'", item.name);
                            logger.error(err);
                        }
                    }
                    if (firstSync) {
                        const state = api.getState();
                        const views = state.views.items;
                        const settings = state.settings;

                        api.dispatch(
                            initializeWorkspaceFromSettings({
                                settings: settings,
                                views: settings.workspaceConfig.views
                                    .map((x) => ({ ...x, view: views.find((v) => v.id === x.viewId) }))
                                    .filter((x) => x.view)
                                    .map((x) => ({ workspaceId: x.workspaceId, view: x.view! })),
                                defaultView: views.find((x) => !x.id)!,
                                isDark: state.settings.isDark
                            })
                        );
                        // First sync completed
                        api.dispatch(setFirstSyncCompleted());
                    }
                    firstSync = false;

                    await api.delay(UPDATE_PERIOD_SECONDS * 1000);
                }
            } catch (err) {
                // Cancelled
                logger.error(err);
            } finally {
                for (const task of syncTasks) {
                    task.cleanup?.();
                }
            }
        }
    });
}

interface SyncTask<T = unknown> {
    readonly name: string;
    run: (args: { firstSync: boolean; dispatch: AppDispatch; state?: T; getState: () => T }) => Promise<T | void>;
    cleanup?: () => void;
}

const settingsTask: SyncTask<RootState> = {
    name: 'User Settings',
    run: async (args) => {
        if (!args.firstSync) {
            return;
        }

        const settings = await SettingsService.getUserSettingsAsync({ withAuthorization: true });
        const items = settings.items ?? [];

        args.dispatch(initializeSettings(items));
        if (args.getState().settings.autoStartSlideshow) {
            args.dispatch(setSlideshow({ active: true }));
        }
    }
};

const viewsSyncTask: SyncTask<{ lastMonUpdateTime: string; lastUdViewUpdateTime: string; lastSettingsUpdateTime: string }> = {
    name: 'Settings sync',
    run: async (args) => {
        const views = await ViewsService.getAllAsync(
            {
                lastUpdate: args.firstSync ? undefined : args.state?.lastUdViewUpdateTime
            },
            {
                withAuthorization: true
            }
        );

        if (views.items) {
            if (args.firstSync) {
                args.dispatch(setViews({ views: views.items }));
            } else {
                if ((views.items.length ?? 0) + (views.deleted?.length ?? 0) > 0) {
                    args.dispatch(processViewUpdates({ views: views.items ?? [], deleted: views.deleted ?? [] }));
                }
            }
        }

        const timeNow = new Date().toISOString();
        return {
            lastMonUpdateTime: timeNow,
            lastSettingsUpdateTime: timeNow,
            lastUdViewUpdateTime: timeNow
        };
    }
};

const monitorSettingsSyncTask: SyncTask = {
    name: 'Monitor settings sync',
    run: async (args) => {
        if (!args.firstSync) {
            return;
        }

        const response = await MonitorSettingsService.getAllAsync({ lastUpdate: undefined }, { withAuthorization: true });
        args.dispatch(setMonitorSettings(response));
    }
};

const filterGroupSyncTask: SyncTask<{ lastSync: string }> = {
    name: 'Filter group sync',
    run: async (args) => {
        const response = await FilterGroupsService.getFilterGroups(
            {
                lastUpdate: args.firstSync ? undefined : args.state?.lastSync
            },
            {
                withAuthorization: true
            }
        );

        if (args.firstSync && response.items) {
            args.dispatch(setFilterGroupsFromList({ groups: response.items }));
        } else {
            if (response.items?.length || response.deletedIds?.length) {
                args.dispatch(
                    processFilterGroupUpdates({
                        groups: response.items ?? [],
                        deleted: response.deletedIds ?? []
                    })
                );
            }
        }

        return {
            lastSync: new Date().toISOString()
        };
    }
};

const drawingObjectSyncTask: SyncTask<{ lastSync: string }> = {
    name: 'Drawing objects',
    run: async (args) => {
        const response = await DrawingObjectsService.getAllAsync(
            {
                lastUpdate: args.firstSync ? undefined : args.state?.lastSync
            },
            { withAuthorization: true }
        );
        if (args.firstSync) {
            args.dispatch(initDrawingObjects(response.drawings ?? []));
        } else {
            args.dispatch(updateDrawingObjects({ updated: response.drawings ?? [], deleted: response.deleted ?? [] }));
        }

        const timeNow = new Date().toISOString();
        return {
            lastSync: timeNow
        };
    }
};

const notesSyncTask: SyncTask<{ lastSync: string }> = {
    name: 'Notes sync',
    run: async (args) => {
        const response = await NotesService.getNotesSummary({ withAuthorization: true });

        args.dispatch(
            setNotesSummary({
                aircraftWithNotes: response.aircraft ?? [],
                airportsWithNotes: response.airports ?? []
            })
        );
    }
};

const mapSettings: SyncTask<{ lastSync: string }> = {
    name: 'Map settings',
    run: async (args) => {
        if (!args.firstSync) {
            return;
        }
        const response = await SettingsService.getMapSettingsAsync({ withAuthorization: true });
        args.dispatch(
            setMapUrlReplacement({
                navSources: response.navSources ?? [],
                baseSources: response.baseSources ?? [],
                tileHost: response.tileUrl ?? ''
            })
        );
    }
};

const customEventsTask: SyncTask = {
    name: 'Custom Events task',
    run: async (args) => {
        if (!args.firstSync) {
            return;
        }

        const response = await SettingsService.getCustomEventsAsync({ withAuthorization: true });
        args.dispatch(
            setCustomEvents(
                response
                    .filter((x) => x.code)
                    .map(
                        (x) =>
                            ({
                                code: x.code!,
                                displayName: x.displayName ?? '',
                                eventListText: x.eventListText ?? '',
                                spokenText: x.spokenText ?? ''
                            }) satisfies ThresholdsCustomEventEntry
                    )
            )
        );
    }
};

const syncTasks: Array<SyncTask<any>> = [
    viewsSyncTask,
    customEventsTask,
    monitorSettingsSyncTask,
    settingsTask,
    filterGroupSyncTask,
    drawingObjectSyncTask,
    notesSyncTask,
    mapSettings
];
